import React from 'react';

// Continut SEO pentru homepage (pre-randat de react-snap, vizibil pentru Google fara JavaScript).
// Reguli continut: fara "ghiseu", fara taxa ONRC/defalcare pret, fara comparatie cu portalul ONRC.

const H2 = ({ children }) => (
  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-12 mb-4">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="text-xl font-bold text-gray-900 mt-6 mb-2">{children}</h3>
);
const P = ({ children }) => <p className="mb-4 leading-relaxed">{children}</p>;
const A = ({ href, children }) => (
  <a href={href} className="text-emerald-700 font-medium underline decoration-emerald-300 underline-offset-2 hover:text-emerald-900">
    {children}
  </a>
);

const SeoContent = () => (
  <section className="bg-white py-16 px-4">
    <div className="max-w-4xl mx-auto text-gray-700">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
        Certificat constatator online, fără portaluri greoaie
      </h2>
      <P>
        Ai nevoie de un certificat constatator pentru bancă, pentru o licitație sau pentru un partener nou și nu ai timp
        de birocrație? Pe constatatoare.ro comanzi <strong>certificatul constatator ONRC online</strong> în câteva minute:
        introduci CUI-ul firmei, plătești cu cardul și primești documentul pe email, ca PDF semnat electronic.
        Fără cont, fără portaluri greoaie și fără formulare complicate.
      </P>
      <P>
        Certificatul este emis de Oficiul Național al Registrului Comerțului (ONRC), este semnat electronic cu semnătură
        calificată și are aceeași valoare juridică precum varianta tipărită. Constatatoare.ro <strong>nu este ONRC</strong> și
        nici instituție publică: suntem un serviciu privat, operat de BITCRAFTERS SRL, care se ocupă în numele tău de
        toată partea tehnică, astfel încât tu să primești documentul oficial cât mai repede.
      </P>

      <H2>Ce conține certificatul constatator</H2>
      <P>
        Certificatul constatator arată situația la zi a unei firme, așa cum este înregistrată în Registrul Comerțului.
        Conținutul exact depinde de forma juridică a firmei, dar documentul include, de regulă:
      </P>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li><strong>datele de identificare</strong>: denumirea, forma juridică, codul unic de înregistrare (CUI) și numărul de ordine în Registrul Comerțului;</li>
        <li><strong>sediul social</strong> și, acolo unde există, sediile secundare și punctele de lucru;</li>
        <li><strong>asociații</strong> sau acționarii și participarea lor la capitalul social;</li>
        <li><strong>administratorii</strong>, cu durata mandatului și puterile acordate;</li>
        <li><strong>capitalul social</strong> subscris și vărsat;</li>
        <li><strong>activitatea principală și activitățile secundare</strong>, cu codurile CAEN;</li>
        <li><strong>starea firmei</strong>: activă, cu activitatea suspendată, în insolvență sau radiată.</li>
      </ul>
      <P>
        Pentru o persoană fizică autorizată, o întreprindere individuală sau una familială, certificatul cuprinde datele
        titularului, sediul profesional și activitățile autorizate. Certificatul standard se eliberează în formă detaliată (extinsă), cu
        sediile secundare și activitățile autorizate; mai multe despre asta în ghidul despre{' '}
        <A href="/blog/certificat-constatator-forma-extinsa/">certificatul constatator în formă extinsă</A>.
      </P>

      <H2>Certificat constatator de bază sau cu istoric?</H2>
      <P>
        Cea mai frecventă greșeală este comandarea unui alt tip de certificat decât cel cerut. Iată cum alegi corect.
      </P>
      <H3>Certificatul constatator de bază</H3>
      <P>
        Arată situația actuală a firmei și este suficient în marea majoritate a cazurilor: deschiderea unui cont bancar,
        un credit sau un leasing, contracte cu parteneri privați, licitații obișnuite, dosare la notar sau verificarea
        rapidă a unui client ori a unui furnizor. Dacă în cerere scrie doar „certificat constatator”, aceasta este
        varianta potrivită.
      </P>
      <H3>Certificatul constatator cu istoric</H3>
      <P>
        Pe lângă situația actuală, conține toate modificările înregistrate de la înființarea firmei: schimbări de asociați,
        de administratori, de sediu, de capital social, de denumire sau de obiect de activitate. Este cerut pentru
        achiziția unei firme, pentru due diligence, în unele proceduri de achiziție publică sau în dosare aflate în instanță.
      </P>
      <P>
        <strong>Regula simplă:</strong> citește exact formularea din cererea instituției. Dacă apare cuvântul „istoric”,
        comandă certificatul cu istoric; altfel, certificatul de bază este alegerea corectă.
      </P>

      <H2>Cum comanzi certificatul constatator online în 4 pași</H2>
      <ol className="list-decimal pl-6 mb-4 space-y-3">
        <li>
          <strong>Introduci CUI-ul firmei.</strong> Datele firmei se completează automat, așa că vezi imediat dacă ai
          ales firma corectă. Pentru persoane fizice, introduci CNP-ul.
        </li>
        <li>
          <strong>Alegi tipul de certificat</strong>: de bază sau cu istoric, plus scopul pentru care îl soliciți.
        </li>
        <li>
          <strong>Completezi datele de facturare și de contact.</strong> Factura poate fi emisă pe firmă sau pe persoană fizică.
        </li>
        <li>
          <strong>Plătești cu cardul</strong>, prin Stripe, și primești certificatul pe email în format PDF, de regulă în
          aproximativ 5 minute, împreună cu factura.
        </li>
      </ol>
      <P>
        Datele introduse sunt verificate înainte de plată. Dacă un CUI nu există sau nu corespunde, primești imediat un
        mesaj de eroare, astfel încât nu plătești pentru un document greșit.
      </P>

      <H2>Cât costă certificatul constatator</H2>
      <P>
        Prețul este afișat clar, fără surprize la final. <strong>Certificatul constatator de bază costă 87 lei + TVA</strong>,
        cu taxa ONRC inclusă și fără taxă de urgență: toate comenzile sunt procesate imediat, la același preț.
        Certificatul cu istoric costă 449 lei, tot cu taxa ONRC inclusă. Plata se face online, cu cardul, iar factura
        fiscală se emite automat și ajunge pe email.
      </P>
      <P>
        Când compari mai multe servicii online, verifică dacă prețul afișat include TVA-ul și dacă procesarea rapidă
        se plătește separat. Un preț de bază mic poate deveni mai mare la final, după ce se adaugă TVA-ul și taxa de urgență.
      </P>

      <H2>Cât timp este valabil certificatul constatator</H2>
      <P>
        Legea nu stabilește o perioadă fixă de valabilitate: certificatul atestă situația firmei la data emiterii. În
        practică, majoritatea instituțiilor, mai ales băncile, acceptă certificate emise în ultimele 30 de zile. La
        licitațiile publice, vechimea acceptată este precizată în documentația de atribuire, iar notarii cer adesea un
        certificat foarte recent.
      </P>
      <P>
        Dacă firma a înregistrat între timp o modificare, de exemplu un sediu nou sau un alt administrator, ai nevoie de
        un certificat nou, emis după modificare. Detalii despre cerințele fiecărei instituții găsești în ghidul despre{' '}
        <A href="/blog/eliberare-certificat-constatator-onrc/">eliberarea certificatului constatator ONRC</A>.
      </P>

      <H2>Greșeli frecvente când comanzi un certificat constatator</H2>
      <P>
        Cele mai multe dosare respinse au la bază câteva greșeli simple, ușor de evitat:
      </P>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li>
          <strong>Certificatul este prea vechi.</strong> Un document emis acum două luni poate fi refuzat, chiar dacă datele
          firmei nu s-au schimbat. Comandă-l cu puțin timp înainte de depunerea dosarului.
        </li>
        <li>
          <strong>Tipul greșit de certificat.</strong> Dacă instituția cere un certificat cu istoric, certificatul
          standard (situația la zi) nu va fi acceptat.
        </li>
        <li>
          <strong>Certificat emis înainte de o modificare.</strong> După schimbarea sediului, a administratorului sau a
          asociaților, ai nevoie de un certificat nou, care să reflecte situația actuală.
        </li>
        <li>
          <strong>CUI introdus greșit.</strong> O singură cifră greșită înseamnă un certificat pentru o altă firmă. De aceea,
          înainte de plată îți afișăm denumirea firmei găsite după CUI.
        </li>
      </ul>

      <H2>Verifică un partener de afaceri înainte să semnezi</H2>
      <P>
        Certificatul constatator nu este util doar pentru propria firmă. Informațiile din Registrul Comerțului sunt
        publice, așa că poți cere certificat pentru orice societate înregistrată în România. Înainte de un contract
        important, de o livrare pe credit sau de un parteneriat pe termen lung, un certificat constatator îți arată în
        câteva minute dacă firma este activă, cine o administrează, cine sunt asociații și dacă a intrat în insolvență.
        Este o verificare simplă, care te poate scuti de pierderi mult mai mari.
      </P>

      <H2>Pentru cine este certificatul constatator online</H2>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li>
          <strong>Antreprenori și administratori de SRL-uri</strong> care au nevoie rapid de document pentru bancă,
          finanțări sau contracte.
        </li>
        <li>
          <strong>Persoane fizice autorizate</strong> (PFA, întreprinderi individuale și familiale). Detalii în ghidul despre{' '}
          <A href="/blog/certificat-constatator-persoana-fizica/">certificatul constatator pentru PFA</A>.
        </li>
        <li>
          <strong>Contabili și firme de contabilitate</strong> care pregătesc dosare pentru clienții lor și nu vor să
          piardă timp cu birocrația.
        </li>
        <li>
          <strong>Avocați și notari</strong> care verifică reprezentanții legali și situația unei societăți înainte de o tranzacție.
        </li>
        <li>
          <strong>Departamente de achiziții și juridice</strong> care verifică furnizorii și partenerii înainte de semnarea contractelor.
        </li>
        <li>
          <strong>Consultanți în fonduri europene</strong> care pregătesc dosare de finanțare și au nevoie de date oficiale la zi.
        </li>
        <li>
          <strong>Firme care deschid un punct de lucru</strong>; vezi și ghidul pentru{' '}
          <A href="/blog/constatator-punct-de-lucru/">certificatul constatator pentru punct de lucru</A>.
        </li>
      </ul>

      <H2>Certificat constatator online în toată România</H2>
      <P>
        Serviciul funcționează pentru orice firmă înregistrată la Registrul Comerțului din România, indiferent de județul
        în care își are sediul. Comanzi la fel de simplu un{' '}
        <A href="/certificat-constatator/bucuresti/">certificat constatator în București</A>, în{' '}
        <A href="/certificat-constatator/cluj-napoca/">Cluj-Napoca</A>,{' '}
        <A href="/certificat-constatator/timisoara/">Timișoara</A>,{' '}
        <A href="/certificat-constatator/iasi/">Iași</A>,{' '}
        <A href="/certificat-constatator/constanta/">Constanța</A> sau{' '}
        <A href="/certificat-constatator/brasov/">Brașov</A>, ca și în orice alt oraș din țară. Documentul ajunge pe email,
        așa că poți comanda de oriunde, de pe calculator sau de pe telefon.
      </P>

      <H2>De ce să comanzi certificatul constatator prin constatatoare.ro</H2>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li><strong>Document oficial ONRC</strong>, semnat electronic, verificabil pe site-ul ONRC cu codul de bare de pe prima pagină.</li>
        <li><strong>Rapid</strong>: certificatul ajunge pe email, de regulă în aproximativ 5 minute.</li>
        <li><strong>Preț clar</strong>: 87 lei + TVA, taxa ONRC inclusă, fără taxă de urgență și fără costuri ascunse.</li>
        <li><strong>Fără cont și fără portaluri greoaie</strong>: introduci CUI-ul, plătești și ai terminat.</li>
        <li><strong>Plată securizată</strong> prin Stripe, cu card Visa, Mastercard sau Maestro.</li>
        <li><strong>Factură fiscală automată</strong>, trimisă pe email, gata pentru contabilitate.</li>
        <li><strong>Verificarea datelor înainte de plată</strong>, ca să nu plătești pentru un certificat greșit.</li>
        <li><strong>Suport real</strong> la telefon și pe email, de luni până vineri, între 09:00 și 17:00.</li>
        <li><strong>Firmă românească</strong>: serviciul este operat de BITCRAFTERS SRL, CUI RO49900684.</li>
      </ul>
      <P>
        Mai ai întrebări? Găsești mai jos răspunsurile la cele mai frecvente întrebări sau poți citi{' '}
        <A href="/blog/">ghidurile noastre despre certificatul constatator</A>.
      </P>
    </div>
  </section>
);

export default SeoContent;
